import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// The builder's "Add Fields" modal (TASK-267.5). Left: a type search + list;
// Enter (or a click) clones that type's <template> into the "Adding" list on
// the right. The footer button counts the rows; the form posts them all in one
// request. Choice rows carry their own "Options — one per line" textarea.
export default class extends Controller {
  static targets = ["search", "types", "list", "empty", "submit", "noMatch"]

  connect() {
    this.sortable = Sortable.create(this.listTarget, {
      handle: "[data-add-fields-grip]",
      animation: 150
    })
    this.filter()
    // The modal animates open; focus once it is there.
    setTimeout(() => this.searchTarget.focus(), 50)
  }

  disconnect() {
    if (this.sortable) this.sortable.destroy()
  }

  get items() {
    return Array.from(this.typesTarget.querySelectorAll("[data-add-fields-type]"))
  }

  get visibleItems() {
    return this.items.filter((el) => !el.classList.contains("hidden"))
  }

  filter() {
    const q = this.searchTarget.value.trim().toLowerCase()
    this.items.forEach((el) => {
      el.classList.toggle("hidden", q !== "" && !el.dataset.name.includes(q))
    })
    this.typesTarget.querySelectorAll("[data-add-fields-group]").forEach((group) => {
      const any = group.querySelector("[data-add-fields-type]:not(.hidden)")
      group.classList.toggle("hidden", !any)
    })
    this.noMatchTarget.classList.toggle("hidden", this.visibleItems.length > 0)

    // The top match is the one Enter adds.
    const top = q === "" ? null : this.visibleItems[0]
    this.items.forEach((el) => {
      el.classList.toggle("is-top", el === top)
      el.querySelector("[data-add-fields-enter-chip]")?.classList.toggle("hidden", el !== top)
    })
    this.top = top
  }

  searchKey(event) {
    if (event.key !== "Enter") return
    event.preventDefault()
    const target = this.top || (this.searchTarget.value.trim() === "" ? null : this.visibleItems[0])
    if (target) this.addType(target.dataset.addFieldsType)
  }

  add(event) {
    event.preventDefault()
    this.addType(event.currentTarget.dataset.addFieldsType)
  }

  addType(type) {
    const template = this.element.querySelector(`template[data-add-fields-template="${CSS.escape(type)}"]`)
    if (!template) return

    const row = template.content.firstElementChild.cloneNode(true)
    this.listTarget.appendChild(row)
    this.searchTarget.value = ""
    this.filter()
    this.refresh()

    const label = row.querySelector("[data-add-fields-label]")
    label.focus()
    row.scrollIntoView({ block: "nearest" })
  }

  remove(event) {
    event.preventDefault()
    event.currentTarget.closest("[data-add-fields-row]").remove()
    this.refresh()
    this.searchTarget.focus()
  }

  // Enter in a label goes back to search, ready for the next type.
  labelKey(event) {
    if (event.key !== "Enter") return
    event.preventDefault()
    this.searchTarget.focus()
  }

  // A stray Enter in any single-line input must not submit the form; the
  // footer button is the only way to submit.
  guardEnter(event) {
    if (event.key === "Enter" && event.target.tagName === "INPUT") event.preventDefault()
  }

  refresh() {
    const count = this.listTarget.querySelectorAll("[data-add-fields-row]").length
    this.emptyTarget.classList.toggle("hidden", count > 0)
    this.submitTarget.disabled = count === 0
    this.submitTarget.textContent = `Add ${count} ${count === 1 ? "Field" : "Fields"}`
  }
}
